import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ApiOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  ReloadOutlined,
  SaveOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { get, post, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './PaymentManagementPage.css';

const { Text } = Typography;

interface PaymentConfig {
  id: number;
  enabled: boolean;
  test_mode: boolean;
  base_url: string;
  app_id: string;
  callback_url: string;
  timeout_seconds: number;
  default_account_id: number;
  has_app_secret: boolean;
  app_secret_masked: string;
  last_test_status?: string;
  last_test_at?: string;
}

interface PaymentConfigForm extends Omit<PaymentConfig, 'id' | 'has_app_secret' | 'app_secret_masked' | 'last_test_status' | 'last_test_at'> {
  app_secret?: string;
  clear_app_secret?: boolean;
}

interface PaymentChannel {
  amountOptions: number[];
  channelCode: string;
  isSupported: boolean;
  name: string;
}

interface PaymentTestResult {
  success: boolean;
  message: string;
  elapsed_ms: number;
  data?: any;
  raw?: any;
  record_id?: number;
}

interface PaymentTestRecord {
  id: number;
  action: 'channels' | 'order' | 'callback' | 'provider_callback';
  success: boolean;
  message: string;
  elapsed_ms: number;
  order_no?: string;
  platform_no?: string;
  request?: any;
  response?: any;
  created_at: string;
}

interface ListResponse<T> {
  data: T[];
  total: number;
}

const actionLabels: Record<PaymentTestRecord['action'], string> = {
  channels: '通道查询',
  order: '创建订单',
  callback: '模拟渠道回调',
  provider_callback: '产品回调',
};

const dateText = (value?: string) => (value ? new Date(value).toLocaleString() : '-');

const RawResult: React.FC<{ value: any }> = ({ value }) => (
  <details className="payment-raw-result">
    <summary>查看原始返回</summary>
    <pre>{JSON.stringify(value, null, 2)}</pre>
  </details>
);

const PaymentManagementPage: React.FC = () => {
  const [configForm] = Form.useForm<PaymentConfigForm>();
  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingChannels, setTestingChannels] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [testingCallback, setTestingCallback] = useState(false);
  const [amountText, setAmountText] = useState('19, 59, 199');
  const [channels, setChannels] = useState<PaymentChannel[]>([]);
  const [channelResult, setChannelResult] = useState<PaymentTestResult | null>(null);
  const [orderAmount, setOrderAmount] = useState(19);
  const [paymentType, setPaymentType] = useState('');
  const [testUserId, setTestUserId] = useState('ADMIN-TEST-USER');
  const [orderResult, setOrderResult] = useState<PaymentTestResult | null>(null);
  const [callbackResult, setCallbackResult] = useState<PaymentTestResult | null>(null);
  const [records, setRecords] = useState<PaymentTestRecord[]>([]);

  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [setting, history] = await Promise.all([
        get<PaymentConfig>(API_ENDPOINTS.paymentManagement.config),
        get<ListResponse<PaymentTestRecord>>(API_ENDPOINTS.paymentManagement.records),
      ]);
      setConfig(setting);
      setRecords(history.data || []);
      configForm.setFieldsValue({
        enabled: setting.enabled,
        test_mode: setting.test_mode,
        base_url: setting.base_url,
        app_id: setting.app_id || 'AI-0001',
        callback_url: setting.callback_url,
        timeout_seconds: setting.timeout_seconds,
        default_account_id: setting.default_account_id || 0,
        app_secret: '',
        clear_app_secret: false,
      });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载支付管理数据失败');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [configForm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveConfig = async (values: PaymentConfigForm) => {
    setSaving(true);
    try {
      const saved = await put<PaymentConfig>(API_ENDPOINTS.paymentManagement.config, values);
      setConfig(saved);
      configForm.setFieldsValue({ ...values, app_secret: '', clear_app_secret: false });
      message.success('支付接入配置已保存');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存支付配置失败');
    } finally {
      setSaving(false);
    }
  };

  const parseAmounts = () => {
    const amounts = Array.from(
      new Set(
        amountText
          .split(/[,，\s]+/)
          .map((item) => Number(item))
          .filter((item) => Number.isFinite(item) && item > 0)
      )
    );
    if (!amounts.length) throw new Error('请输入至少一个有效金额');
    return amounts;
  };

  const testChannels = async () => {
    setTestingChannels(true);
    try {
      const result = await post<PaymentTestResult>(API_ENDPOINTS.paymentManagement.testChannels, {
        amounts: parseAmounts(),
      });
      const nextChannels = Array.isArray(result.data) ? result.data : [];
      setChannelResult(result);
      setChannels(nextChannels);
      const firstSupported = nextChannels.find((item: PaymentChannel) => item.isSupported);
      if (firstSupported) setPaymentType(firstSupported.channelCode);
      message.success('支付通道查询成功');
      loadData(false);
    } catch (error) {
      setChannelResult(null);
      message.error(error instanceof Error ? error.message : '支付通道查询失败');
    } finally {
      setTestingChannels(false);
    }
  };

  const createOrder = async () => {
    if (!paymentType.trim()) {
      message.warning('请先选择或输入支付通道');
      return;
    }
    setCreatingOrder(true);
    setCallbackResult(null);
    try {
      const result = await post<PaymentTestResult>(API_ENDPOINTS.paymentManagement.testOrders, {
        amount: orderAmount,
        payment_type: paymentType,
        user_id: testUserId,
        order_type: 'recharge',
      });
      setOrderResult(result);
      message.success('测试订单创建成功');
      loadData(false);
    } catch (error) {
      setOrderResult(null);
      message.error(error instanceof Error ? error.message : '测试订单创建失败');
    } finally {
      setCreatingOrder(false);
    }
  };

  const testCallback = async () => {
    if (!orderResult?.record_id) {
      message.warning('请先创建一笔测试订单');
      return;
    }
    setTestingCallback(true);
    try {
      const result = await post<PaymentTestResult>(API_ENDPOINTS.paymentManagement.testCallbacks, {
        test_record_id: orderResult.record_id,
        signature: 'mock-signature',
      });
      setCallbackResult(result);
      message.success('模拟支付回调成功');
      loadData(false);
    } catch (error) {
      setCallbackResult(null);
      message.error(error instanceof Error ? error.message : '模拟支付回调失败');
    } finally {
      setTestingCallback(false);
    }
  };

  const channelColumns: ColumnsType<PaymentChannel> = [
    { title: '通道', dataIndex: 'name', render: (value, row) => <><Text strong>{value || row.channelCode}</Text><div className="payment-muted">{row.channelCode}</div></> },
    { title: '状态', dataIndex: 'isSupported', width: 120, render: (value) => <Tag color={value ? 'success' : 'default'}>{value ? '可用' : '不支持'}</Tag> },
    { title: '支持金额', dataIndex: 'amountOptions', render: (values: number[]) => values?.length ? values.map((value) => <Tag key={value}>¥{value}</Tag>) : '-' },
    { title: '操作', width: 120, render: (_, row) => <Button type="link" disabled={!row.isSupported} onClick={() => setPaymentType(row.channelCode)}>用于下单</Button> },
  ];

  const recordColumns: ColumnsType<PaymentTestRecord> = [
    { title: '步骤', dataIndex: 'action', width: 150, render: (value: PaymentTestRecord['action']) => actionLabels[value] || value },
    { title: '状态', dataIndex: 'success', width: 100, render: (value) => <Tag color={value ? 'success' : 'error'}>{value ? '成功' : '失败'}</Tag> },
    { title: '结果', dataIndex: 'message', ellipsis: true },
    { title: '业务订单号', dataIndex: 'order_no', width: 230, render: (value) => value || '-' },
    { title: '支付中心单号', dataIndex: 'platform_no', width: 210, render: (value) => value || '-' },
    { title: '耗时', dataIndex: 'elapsed_ms', width: 100, render: (value) => `${value || 0} ms` },
    { title: '时间', dataIndex: 'created_at', width: 180, render: dateText },
  ];

  const channelOptions = useMemo(
    () => channels.filter((item) => item.isSupported).map((item) => ({ label: `${item.name} (${item.channelCode})`, value: item.channelCode })),
    [channels]
  );

  const configPanel = (
    <section className="payment-section">
      <div className="payment-section-heading">
        <div>
          <h2>支付中心接入配置</h2>
          <p>Secret 仅发送给灵笔管理端后端，读取配置时只返回掩码。</p>
        </div>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => configForm.submit()}>保存配置</Button>
      </div>
      <Form
        form={configForm}
        layout="vertical"
        onFinish={saveConfig}
        initialValues={{ enabled: false, test_mode: true, base_url: 'https://polaris-center-ht.cestalt.com', app_id: 'AI-0001', timeout_seconds: 20, default_account_id: 0 }}
      >
        <div className="payment-config-grid">
          <Form.Item name="enabled" label="启用支付接入" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item name="test_mode" label="测试模式" valuePropName="checked">
            <Switch checkedChildren="测试" unCheckedChildren="正式" />
          </Form.Item>
          <Form.Item name="timeout_seconds" label="接口超时（秒）">
            <InputNumber min={3} max={60} />
          </Form.Item>
          <Form.Item
            name="default_account_id"
            label="默认鹿鸣 accountId"
            extra="用于未经过鹿鸣 SSO 的普通登录用户；填写 0 时默认使用当前灵笔用户 ID。已绑定用户始终优先使用自己的鹿鸣 accountId。"
          >
            <InputNumber min={0} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item className="payment-span-2" name="base_url" label="支付中心地址" rules={[{ required: true, message: '请输入支付中心地址' }]}>
            <Input prefix={<ApiOutlined />} placeholder="https://polaris-center-ht.cestalt.com" />
          </Form.Item>
          <Form.Item name="app_id" label="App ID" rules={[{ required: true, message: '请输入 App ID' }]}>
            <Input placeholder="AI-0001" />
          </Form.Item>
          <Form.Item name="app_secret" label={`App Secret${config?.has_app_secret ? `（已配置 ${config.app_secret_masked}）` : ''}`}>
            <Input.Password autoComplete="new-password" placeholder={config?.has_app_secret ? '留空则保留原 Secret' : '请输入 App Secret'} />
          </Form.Item>
          <Form.Item name="clear_app_secret" label="清除已有 Secret" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item className="payment-span-3" name="callback_url" label="产品回调地址">
            <Input placeholder="https://你的管理API/api/v1/payment_management/callback/polaris/AI-0001" />
          </Form.Item>
        </div>
      </Form>
      <Alert
        type={config?.last_test_status?.includes('成功') ? 'success' : 'info'}
        showIcon
        message={config?.last_test_status || '尚未执行支付接口测试'}
        description={config?.last_test_at ? `最近测试：${dateText(config.last_test_at)}` : '保存配置后到“联调测试”验证通道和订单。'}
      />
    </section>
  );

  const testPanel = (
    <div className="payment-test-stack">
      <Alert
        type="warning"
        showIcon
        message="当前只做管理后台联调，不会修改用户余额，也不会接入用户端充值。"
        description="请保持测试模式开启。支付中心测试环境会模拟成功，正式环境是否真实扣款以第三方配置为准。"
      />

      <section className="payment-section">
        <div className="payment-section-heading">
          <div><h2>1. 查询可用支付通道</h2><p>一次查询多个金额，确认应用绑定了哪些支付方式。</p></div>
          <Button type="primary" icon={<ApiOutlined />} loading={testingChannels} onClick={testChannels}>查询通道</Button>
        </div>
        <label className="payment-field-label" htmlFor="payment-amounts">测试金额</label>
        <Input id="payment-amounts" value={amountText} onChange={(event) => setAmountText(event.target.value)} placeholder="19, 59, 199" />
        <Table rowKey="channelCode" columns={channelColumns} dataSource={channels} pagination={false} className="payment-channel-table" />
        {channelResult && <RawResult value={channelResult.raw} />}
      </section>

      <section className="payment-section">
        <div className="payment-section-heading">
          <div><h2>2. 创建测试订单</h2><p>使用已验证的通道创建充值测试订单，订单号由后端自动生成。</p></div>
          <Button type="primary" icon={<CreditCardOutlined />} loading={creatingOrder} onClick={createOrder}>创建订单</Button>
        </div>
        <div className="payment-order-grid">
          <div>
            <label className="payment-field-label" htmlFor="payment-order-amount">订单金额</label>
            <InputNumber id="payment-order-amount" min={0.01} precision={2} value={orderAmount} onChange={(value) => setOrderAmount(value || 0)} prefix="¥" />
          </div>
          <div>
            <label className="payment-field-label">支付通道</label>
            <Select
              showSearch
              value={paymentType || undefined}
              options={channelOptions}
              onChange={setPaymentType}
              placeholder="先查询通道，或输入通道代码"
              optionFilterProp="label"
              dropdownRender={(menu) => menu}
            />
            {!channelOptions.length && <Input className="payment-channel-fallback" value={paymentType} onChange={(event) => setPaymentType(event.target.value)} placeholder="例如 alipay" />}
          </div>
          <div>
            <label className="payment-field-label" htmlFor="payment-test-user">测试用户 ID</label>
            <Input id="payment-test-user" value={testUserId} onChange={(event) => setTestUserId(event.target.value)} />
          </div>
        </div>
        {orderResult && (
          <div className="payment-success-strip">
            <CheckCircleOutlined />
            <div>
              <strong>{orderResult.message}</strong>
              <span>记录 ID：{orderResult.record_id || '-'} · 支付地址：{orderResult.data?.paymentUrl || '未返回'}</span>
            </div>
          </div>
        )}
        {orderResult && <RawResult value={orderResult.raw} />}
      </section>

      <section className="payment-section">
        <div className="payment-section-heading">
          <div><h2>3. 模拟支付成功回调</h2><p>基于上一步订单调用第三方模拟回调，验证订单幂等和产品回调队列。</p></div>
          <Button icon={<SendOutlined />} loading={testingCallback} disabled={!orderResult?.record_id} onClick={testCallback}>模拟回调</Button>
        </div>
        {callbackResult ? (
          <>
            <Alert type="success" showIcon message={callbackResult.message} description={`耗时 ${callbackResult.elapsed_ms} ms`} />
            <RawResult value={callbackResult.raw} />
          </>
        ) : (
          <Text type="secondary">创建测试订单后可执行此步骤。</Text>
        )}
      </section>
    </div>
  );

  const recordPanel = (
    <section className="payment-section payment-record-section">
      <div className="payment-section-heading">
        <div><h2>支付联调记录</h2><p>保存脱敏后的请求、响应、订单号和测试结果。</p></div>
        <Button icon={<ReloadOutlined />} onClick={() => loadData(false)}>刷新</Button>
      </div>
      <Table
        rowKey="id"
        columns={recordColumns}
        dataSource={records}
        loading={loading}
        scroll={{ x: 1180 }}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        expandable={{
          expandedRowRender: (record) => (
            <div className="payment-record-detail">
              <div><Text strong>请求</Text><pre>{JSON.stringify(record.request, null, 2)}</pre></div>
              <div><Text strong>响应</Text><pre>{JSON.stringify(record.response, null, 2)}</pre></div>
            </div>
          ),
        }}
        locale={{ emptyText: '暂无支付联调记录' }}
      />
    </section>
  );

  return (
    <div className="payment-management-page">
      <header className="payment-page-header">
        <div>
          <h1><CreditCardOutlined /> 支付管理</h1>
          <p>配置北极星支付中心，并在管理后台跑通通道查询、测试订单和模拟回调。</p>
        </div>
        <Space>
          <Tag color={config?.enabled ? 'success' : 'default'}>{config?.enabled ? '接入已启用' : '接入未启用'}</Tag>
          <Tag color={config?.test_mode ? 'processing' : 'warning'}>{config?.test_mode ? '测试模式' : '正式模式'}</Tag>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadData()}>刷新</Button>
        </Space>
      </header>
      <Tabs
        defaultActiveKey="config"
        items={[
          { key: 'config', label: '接入配置', children: configPanel },
          { key: 'test', label: '联调测试', children: testPanel },
          { key: 'records', label: `调用记录 ${records.length ? `(${records.length})` : ''}`, children: recordPanel },
        ]}
      />
    </div>
  );
};

export default PaymentManagementPage;
